// capture.js — getting a picture of the screen into a JPEG data URL.
//
// TWO WAYS, AND THE DEFAULT IS THE ONE WITH NO PROMPT (feedback #582).
// - snapshotPage() re-draws the visible window from the DOM (html2canvas). No
//   browser prompt — Safari asks to share the screen on EVERY capture and has
//   no "always allow". The cost: it can be wrong with no sign of it. Mapbox
//   maps (WebGL), PDFs and Office files in iframes, and some cross-origin
//   images (Blob Storage) can come out blank. The dialog says so.
// - captureScreen() is the browser's own screen share (getDisplayMedia). It
//   copies real pixels, so it is right on maps and previews, at the cost of one
//   browser prompt. Kept as the "exact capture" fallback.
// Where neither fits, the person can paste or upload a screenshot instead.

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.82;

export const canCaptureScreen = () =>
  typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);

// Scale a drawable (video frame, image) onto a canvas no wider than MAX_WIDTH.
export function scaledSize(w, h, maxWidth = MAX_WIDTH) {
  if (!w || !h) return { width: 0, height: 0 };
  if (w <= maxWidth) return { width: w, height: h };
  return { width: maxWidth, height: Math.round((h * maxWidth) / w) };
}

function toCanvas(source, w, h) {
  const { width, height } = scaledSize(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  canvas.getContext('2d').drawImage(source, 0, 0, width, height);
  return canvas;
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// One frame of the current tab. The caller hides its own dialog first.
export async function captureScreen() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser', frameRate: 5 },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  });
  try {
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    // The browser's own "sharing this tab" bar takes a moment to settle.
    await wait(350);
    return toCanvas(video, video.videoWidth, video.videoHeight).toDataURL('image/jpeg', JPEG_QUALITY);
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

// Shown under "Capture this screen", so a blank map in a snapshot is not a surprise.
export const SNAPSHOT_HINT = 'Captures straight away, with no browser prompt. Maps, PDF and Office previews, '
  + 'and some images may come out blank in a quick snapshot — if that part matters, paste a real screenshot '
  + '(Ctrl/⌘ + V; on a Mac ⌘⇧4 takes one) or upload one instead.';

// html2canvas options for the visible part of the page only (the screen share
// captured the window, not the whole scrolled document), at a resolution that
// lands near MAX_WIDTH rather than rendering a retina-sized canvas to shrink.
// Elements marked data-feedback-skip (the Feedback dialog) are left out.
export function snapshotOptions(view, maxWidth = MAX_WIDTH) {
  const width = Math.max(1, Math.round(view.width || 0));
  const height = Math.max(1, Math.round(view.height || 0));
  const dpr = view.devicePixelRatio || 1;
  const scale = Math.min(dpr, maxWidth / width);
  return {
    x: Math.round(view.scrollX || 0),
    y: Math.round(view.scrollY || 0),
    width,
    height,
    windowWidth: width,
    windowHeight: height,
    scale: scale > 0 ? scale : 1,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => !!(el && el.hasAttribute && el.hasAttribute('data-feedback-skip')),
  };
}

// A quick snapshot of the visible window, with no browser prompt. Loaded on
// demand so no other page pays for the library. The caller hides its dialog.
export async function snapshotPage() {
  const mod = await import('html2canvas');
  const html2canvas = mod.default || mod;
  const canvas = await html2canvas(document.body, snapshotOptions({
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    devicePixelRatio: window.devicePixelRatio,
  }));
  if (!canvas.width || !canvas.height) throw new Error('The snapshot came out empty.');
  return toCanvas(canvas, canvas.width, canvas.height).toDataURL('image/jpeg', JPEG_QUALITY);
}

// A pasted or uploaded image file, normalized to a bounded JPEG.
export function imageFileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file || !/^image\//.test(file.type)) { reject(new Error('That is not an image.')); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try { resolve(toCanvas(img, img.naturalWidth, img.naturalHeight).toDataURL('image/jpeg', JPEG_QUALITY)); }
      catch (e) { reject(e); }
      finally { URL.revokeObjectURL(url); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image could not be read.')); };
    img.src = url;
  });
}

// Burn the highlight boxes into the image, so what the person marked is what
// Claude sees. Boxes are in the image's own pixel coordinates.
export function drawHighlights(dataUrl, boxes) {
  if (!boxes || !boxes.length) return Promise.resolve(dataUrl);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      ctx.lineWidth = Math.max(3, Math.round(canvas.width / 400));
      ctx.strokeStyle = '#e11d48';
      boxes.forEach((b) => ctx.strokeRect(b.x, b.y, b.w, b.h));
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.onerror = () => reject(new Error('Could not apply the highlights.'));
    img.src = dataUrl;
  });
}

// Normalize a drag from (x0,y0) to (x1,y1) into a positive-size box, or null
// when it is too small to be deliberate.
export function boxFromDrag(x0, y0, x1, y1, min = 6) {
  const x = Math.min(x0, x1); const y = Math.min(y0, y1);
  const w = Math.abs(x1 - x0); const h = Math.abs(y1 - y0);
  return w < min || h < min ? null : { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}
